import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

// Original procedural fixture: canonical six-joint skeleton and immutable clips.
export const JOINTS = ['Hips', 'Head', 'LeftArm', 'RightArm', 'LeftLeg', 'RightLeg'] as const;
const gait = (name: string, sign: number): THREE.NumberKeyframeTrack => new THREE.NumberKeyframeTrack(`.bones[${name}].rotation[x]`, [0, .25, .5, .75, 1], [0, .55 * sign, 0, -.55 * sign, 0]);
export const WALK = new THREE.AnimationClip('walk', 1, [gait('LeftLeg', 1), gait('RightLeg', -1), gait('LeftArm', -1), gait('RightArm', 1)]);
export const IDLE = new THREE.AnimationClip('idle', 2, [new THREE.NumberKeyframeTrack('.bones[Head].rotation[z]', [0, 1, 2], [-.03, .03, -.03])]);

export class Avatar {
  readonly root = new THREE.Group();
  readonly mesh: THREE.SkinnedMesh;
  readonly mixer: THREE.AnimationMixer;
  private idle: THREE.AnimationAction;
  private walk: THREE.AnimationAction;
  private moving = false;
  private disposed = false;
  constructor(color: number) {
    const bones = JOINTS.map(name => { const bone = new THREE.Bone(); bone.name = name; return bone; });
    const hips = bones[0]!; hips.position.y = .92;
    const offsets = [[0, 0, 0], [0, .63, 0], [.36, .42, 0], [-.36, .42, 0], [.17, -.05, 0], [-.17, -.05, 0]];
    for (let i = 1; i < bones.length; i++) { bones[i]!.position.fromArray(offsets[i]!); hips.add(bones[i]!); }
    const parts = [
      [.48, .58, .28, 0, 1.13, 0], [.34, .36, .32, 0, 1.68, 0],
      [.17, .58, .19, .36, 1.08, 0], [.17, .58, .19, -.36, 1.08, 0],
      [.22, .82, .24, .17, .46, 0], [.22, .82, .24, -.17, .46, 0],
    ];
    const geometries = parts.map((part, index) => {
      const g = new THREE.BoxGeometry(part[0], part[1], part[2]); g.translate(part[3]!, part[4]!, part[5]!);
      const count = g.getAttribute('position').count;
      const indices = new Uint16Array(count * 4); const weights = new Float32Array(count * 4);
      for (let i = 0; i < count; i++) { indices[i * 4] = index; weights[i * 4] = 1; }
      g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(indices, 4)); g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(weights, 4));
      return g;
    });
    const geometry = mergeGeometries(geometries); geometries.forEach(g => g.dispose());
    if (!geometry) throw new Error('Could not build avatar fixture');
    this.mesh = new THREE.SkinnedMesh(geometry, new THREE.MeshStandardMaterial({ color, roughness: .7 }));
    this.mesh.add(hips); this.mesh.bind(new THREE.Skeleton(bones)); this.mesh.castShadow = true; this.mesh.frustumCulled = false;
    this.root.add(this.mesh); this.mixer = new THREE.AnimationMixer(this.mesh);
    this.idle = this.mixer.clipAction(IDLE); this.walk = this.mixer.clipAction(WALK); this.idle.play();
  }
  update(dt: number, moving: boolean): void {
    if (this.disposed) return;
    if (moving !== this.moving) {
      (moving ? this.idle : this.walk).fadeOut(.15);
      (moving ? this.walk : this.idle).reset().fadeIn(.15).play(); this.moving = moving;
    }
    this.mixer.update(dt);
  }
  dispose(): void {
    if (this.disposed) return; this.disposed = true;
    this.mixer.stopAllAction(); this.mixer.uncacheRoot(this.mesh);
    this.mesh.skeleton.dispose(); this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose(); this.root.removeFromParent();
  }
}
